import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChatBubbleLeftRightIcon, ArrowPathIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import {
  getPatient, getPatientFile, getPatientCockpit, patientAttachmentUrl, uploadDocument, simedStand, akteAusSimed,
  type SimedStand,
  type PatientDetail as Detail, type PatientFile, type PatientCockpit, type DocumentIngest,
} from '../api/unimedica';
import StatusBadge from '../components/StatusBadge';
import PdfViewer from '../components/PdfViewer';
import AkteFragen from '../components/AkteFragen';
import AkteChats from '../components/AkteChats';
import MarkdownContent from '../components/MarkdownContent';
import { useBranding } from '../contexts/BrandingContext';

// Port aus routes/patienten/[id]/+page.svelte — die Akte als Arbeitsplatz:
// Cockpit (Diagnosen, Medikation, Labor, Allergien), Fragen zur Akte,
// Dokumentliste mit Jahrfilter, Viewer mit Original links und Erkanntem rechts.

/* Die Kategorien aus siMed sind lange Sammelbegriffe („Externe Berichte (Spezialisten, Rhea,
   Hosp"). Als Filterknopf braucht es die Kurzform; der ganze Name steht im Tooltip. */
function kurzeKategorie(k: string): string {
  const vorKlammer = k.split('(')[0].trim();
  const kurz = vorKlammer || k;
  return kurz.length > 26 ? kurz.slice(0, 25) + '…' : kurz;
}

const STRUCTURED_LABEL: Record<string, string> = {
  'akte.md': 'Diagnosen & Probleme (komplett)',
  'labor.md': 'Laborverlauf (komplett)',
  'termine.md': 'Termine & Konsultationen',
};
const TYPE_LABEL: Record<string, string> = {
  rezept: 'Rezept', befund: 'Befund', arztbrief: 'Arztbrief', labor: 'Laborbefund',
  akte: 'Aktenauszug', visite: 'Visite', uebersicht: 'Übersicht', bericht: 'Bericht',
};
// Was der Browser inline zeigen kann. Word/E-Mail/TIFF → extrahierter Text + Download.
const RENDERABLE_ATT = /\.(pdf|jpe?g|png|webp|gif)$/i;
const FLAG_ARROW: Record<string, string> = { hoch: '↑', tief: '↓', normal: '', '': '' };
const HIDDEN_KEYS = new Set(['vorname', 'nachname', 'tentative']);

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso);
};
/** Unix-Sekunden → „heute 08:14“, „gestern 21:03“ oder „11.10.2026 08:14“. */
const zeitpunkt = (s: number) => {
  const d = new Date(s * 1000), h = new Date();
  const uhr = d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  const tag = (x: Date) => x.toDateString();
  if (tag(d) === tag(h)) return `heute ${uhr}`;
  if (tag(d) === tag(new Date(h.getTime() - 86400000))) return `gestern ${uhr}`;
  if (tag(d) === tag(new Date(h.getTime() + 86400000))) return `morgen ${uhr}`;
  return `${d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${uhr}`;
};
const istDokument = (f: PatientFile) => (f.typ ?? '').toLowerCase() === 'dokument';
const anzeigeDatei = (f: PatientFile | null) => (f ? f.vorschau || f.attachment : null);
const aktenNr = (pid: string) => { const n = pid.split('_')[0]; return /^\d+$/.test(n) ? `Akte ${n}` : ''; };

/** RAG-Ballast aus den MD-Quellen entfernen: Frontmatter und Wikilinks. */
function cleanMd(text: string): string {
  let t = text.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '');
  t = t.replace(/\[\[([^\]|]+)\|([^\]]+)\]\]/g, '$2');
  t = t.replace(/\[\[([^\]]+)\]\]/g, '$1');
  return t.trim();
}

function docTitle(f: PatientFile): string {
  if (f.name in STRUCTURED_LABEL) return STRUCTURED_LABEL[f.name];
  // String() erzwingen: YAML macht aus "titel: 2016" eine Zahl.
  const titel = String(f.titel ?? '').trim();
  if (titel) return titel;
  const dn = String(f.dateiname ?? '').trim();
  if (dn) return dn.replace(/\.[a-z0-9]{2,5}$/i, '').replace(/[_]+/g, ' ')
    .replace(/\s*\bvom\s+\d{1,2}[.\-]\d{1,2}[.\-]\d{2,4}\s*$/i, '').replace(/\s{2,}/g, ' ').trim();
  const fg = String(f.fachgebiet ?? '').trim(), typ = String(f.typ ?? '').trim();
  const typL = typ ? (TYPE_LABEL[typ.toLowerCase()] ?? cap(typ)) : '';
  if (typL && fg && fg.toLowerCase() !== typ.toLowerCase()) return `${typL} ${cap(fg)}`;
  if (typL) return typL;
  if (fg) return cap(fg);
  const m = f.name.replace(/\.md$/, '').match(/_([a-z0-9-]+)$/i);
  return m ? cap(m[1].replace(/-/g, ' ')) : f.name.replace(/\.md$/, '');
}

const Shimmer = ({ n = 6 }: { n?: number }) => <div className="space-y-2 p-4">{Array.from({ length: n }, (_, i) => <div key={i} className="shimmer h-4 rounded" />)}</div>;

export default function PatientDetail() {
  const { id } = useParams();
  const pid = id ?? '';
  const [detail, setDetail] = useState<Detail | null>(null);
  const [cockpit, setCockpit] = useState<PatientCockpit | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeFile, setActiveFile] = useState<PatientFile | null>(null);
  const [fileText, setFileText] = useState<string | null>(null);
  const [fileLoading, setFileLoading] = useState(false);
  const [showPdf, setShowPdf] = useState(false);
  const [strukturFehler, setStrukturFehler] = useState<string | null>(null);

  // Stand der siMed-Aktualisierung im Kopf (Christian 12.10.2026: „sehen, wann die Akte zuletzt aktualisiert
  // wurde, und von dort aus aktualisieren“). Gleicher Auftrag wie „Aus siMed aktualisieren“ in der Liste.
  const simedAktiv = !!useBranding().branding.funktionen?.simed;
  const [stand, setStand] = useState<SimedStand | null>(null);
  const [anfordern, setAnfordern] = useState(false);
  const standLaeuft = stand?.status === 'wartend' || stand?.status === 'laeuft';
  useEffect(() => {
    if (!simedAktiv || !pid) return;
    let aus = false;
    simedStand(pid).then((s) => { if (!aus) setStand(s); });
    return () => { aus = true; };
  }, [pid, simedAktiv]);
  useEffect(() => {
    if (!standLaeuft) return;
    const t = setInterval(async () => {
      const s = await simedStand(pid);
      if (!s) return;
      setStand(s);
      if (s.status === 'fertig') {
        const [d, c] = await Promise.all([getPatient(pid), getPatientCockpit(pid)]);
        if (d) setDetail(d);
        setCockpit(c);
      }
    }, 15000);
    return () => clearInterval(t);
  }, [standLaeuft, pid]);
  const aktualisieren = async () => {
    setAnfordern(true);
    try { await akteAusSimed(pid.split('_')[0]); setStand(await simedStand(pid)); }
    finally { setAnfordern(false); }
  };
  /* Dokument direkt in der Akte hochladen (Christian 21.09.: „kann man in der
     patientenübersicht selbst dokumente hochladen. diese werden dann erfasst, erkannt
     indexiert wie die anderen auch"). Derselbe Weg wie im Chat: lesen (Docling/Apple OCR),
     unter Dokumente/ ablegen, Messwerte übernehmen, Akte nachführen. */
  const [ladeDok, setLadeDok] = useState(false);
  const [dokMeldung, setDokMeldung] = useState<{ text: string; ok: boolean } | null>(null);

  const dokumenteHochladen = async (dateien: FileList | null) => {
    if (!dateien?.length || !id) return;
    setLadeDok(true); setDokMeldung(null);
    const ergebnisse: DocumentIngest[] = [];
    try {
      for (const datei of Array.from(dateien)) ergebnisse.push(await uploadDocument(id, datei));
      const werte = ergebnisse.reduce((n, r) => n + (r.messwerte || 0), 0);
      const ungelesen = ergebnisse.filter((r) => !r.gelesen).length;
      setDokMeldung({
        ok: ungelesen === 0,
        text: ergebnisse.length === 1
          ? ergebnisse[0].message
          : `${ergebnisse.length} Dokumente abgelegt` + (werte ? `, ${werte} Messwerte übernommen` : '')
            + (ungelesen ? ` — bei ${ungelesen} liess sich kein Text gewinnen` : ''),
      });
      // Akte und Cockpit neu laden, damit Dokument UND neue Messwerte sofort dastehen.
      const [d, c] = await Promise.all([getPatient(id), getPatientCockpit(id)]);
      setDetail(d); setCockpit(c);
    } catch (e: any) {
      setDokMeldung({ ok: false, text: e?.message ?? String(e) });
    } finally {
      setLadeDok(false);
    }
  };

  // Sortiert wird immer nach Datum. Gewaehlt wird nur, WONACH gefiltert wird:
  // nach Jahr (wann war das) oder nach Kategorie (was ist das). Christian 21.09.
  const [gruppierung, setGruppierung] = useState<'jahr' | 'kategorie'>('jahr');
  const [jahrFilter, setJahrFilter] = useState('');
  const [katFilter, setKatFilter] = useState('');

  const docFiles = useMemo(() => (detail?.files ?? []).filter(istDokument)
    .sort((a, b) => (b.datum ?? '').localeCompare(a.datum ?? '') || b.name.localeCompare(a.name)), [detail]);
  const structuredFiles = useMemo(() => {
    const order = Object.keys(STRUCTURED_LABEL);
    return (detail?.files ?? []).filter((f) => f.name in STRUCTURED_LABEL).sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
  }, [detail]);
  const jahre = useMemo(() => {
    const z = new Map<string, number>();
    for (const f of docFiles) { const j = (f.datum ?? '').slice(0, 4); if (/^(19|20)\d{2}$/.test(j)) z.set(j, (z.get(j) ?? 0) + 1); }
    return [...z.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [docFiles]);
  const ohneDatum = useMemo(() => docFiles.filter((f) => !/^(19|20)\d{2}/.test(f.datum ?? '')).length, [docFiles]);
  /* Die Kategorie steht im Frontmatter jedes Dokuments (Feld `kategorie`, sonst `fachgebiet`).
     In der Datenbank fehlt sie bei Altbestand, auf der Platte ist sie vollständig — und von dort
     kommt diese Liste. */
  const kategorieVon = (f: PatientFile) => ((f.kategorie ?? f.fachgebiet ?? '').trim() || 'Ohne Kategorie');
  const kategorien = useMemo(() => {
    const z = new Map<string, number>();
    for (const f of docFiles) { const k = kategorieVon(f); z.set(k, (z.get(k) ?? 0) + 1); }
    // Häufigste zuerst — in einer Akte mit 225 Dokumenten sucht niemand alphabetisch.
    return [...z.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [docFiles]);
  const sichtbareDocs = useMemo(() => {
    if (gruppierung === 'kategorie') {
      if (!katFilter) return docFiles;
      return docFiles.filter((f) => kategorieVon(f) === katFilter);
    }
    if (!jahrFilter) return docFiles;
    if (jahrFilter === 'ohne') return docFiles.filter((f) => !/^(19|20)\d{2}/.test(f.datum ?? ''));
    return docFiles.filter((f) => (f.datum ?? '').startsWith(jahrFilter));
  }, [docFiles, jahrFilter, katFilter, gruppierung]);
  const stammRows = useMemo(() => Object.entries(detail?.stammdaten ?? {})
    .filter(([k, v]) => !HIDDEN_KEYS.has(k) && v !== null && v !== '' && typeof v !== 'object')
    .map(([k, v]) => [k, String(v)] as [string, string]), [detail]);

  const openFile = useCallback(async (f: PatientFile) => {
    setActiveFile(f);
    const a = anzeigeDatei(f);
    setShowPdf(!!a && RENDERABLE_ATT.test(a));
    setFileText(null);
    setFileLoading(true);
    try { setFileText(await getPatientFile(pid, f.name)); } finally { setFileLoading(false); }
  }, [pid]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [d, c] = await Promise.all([getPatient(pid), getPatientCockpit(pid)]);
        if (cancelled) return;
        setDetail(d); setCockpit(c); setNotFound(!d);
        const first = (d?.files ?? []).filter(istDokument).sort((a, b) => (b.datum ?? '').localeCompare(a.datum ?? ''))[0];
        if (first) void openFile(first);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [pid, openFile]);

  /** Uebersichtsseite oeffnen — mehrere Namen probieren (index.md neu, akte.md alt); nicht schweigen, wenn keine da ist. */
  const openStructured = (...namen: string[]) => {
    for (const n of namen) { const f = detail?.files.find((x) => x.name === n); if (f) { void openFile(f); return; } }
    setStrukturFehler('Die Übersichtsseite dieser Akte fehlt (' + namen.join(' / ') + ').');
    setTimeout(() => setStrukturFehler(null), 6000);
  };

  const displayName = () => {
    const s: Record<string, any> = detail?.stammdaten ?? {};
    const voll = String(s.patient ?? '').trim();
    if (voll) { const t = voll.split(/\s+/); return t.length >= 2 ? `${t.slice(1).join(' ')}, ${t[0]}` : voll; }
    const nach = String(s.nachname ?? s.adr_name ?? '').trim(), vor = String(s.vorname ?? s.adr_vorname ?? '').trim();
    return nach && vor ? `${nach}, ${vor}` : nach || vor || pid;
  };

  if (loading) return <div className="card p-6"><div className="shimmer h-24 rounded" /></div>;
  if (notFound || !detail) return (
    <div className="card p-10 text-center">
      <div className="page-h1 mb-2">Akte nicht gefunden</div>
      <Link to="/patienten" className="text-sm font-medium text-primary-500">← Zur Patientenliste</Link>
    </div>
  );

  const katKnopf = (wert: string, label: string, aktiv: boolean, title?: string) => (
    <button key={wert || 'alle'} onClick={() => setKatFilter(wert)} aria-pressed={aktiv} title={title}
      className={`min-h-9 max-w-full truncate rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${aktiv
        ? 'border-ps-dark-900 bg-ps-dark-900 text-white'
        : 'border-gray-200 text-ps-dark-500 hover:border-primary-300 hover:text-primary-600'}`}>{label}</button>
  );

  const jahrKnopf = (wert: string, label: string, aktiv: boolean, title?: string) => (
    <button key={wert} onClick={() => setJahrFilter(wert)} aria-pressed={aktiv} title={title}
      className={`numerical min-h-9 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${aktiv
        ? 'border-ps-dark-900 bg-ps-dark-900 text-white'
        : 'border-gray-200 text-ps-dark-500 hover:border-primary-300 hover:text-primary-600'}`}>{label}</button>
  );
  const ext = (a: string) => (a.split('.').pop() ?? '').toUpperCase();

  return (
    <div className="space-y-4">
      {strukturFehler && <div className="card border-l-2 border-l-primary-500 p-3 text-sm text-ps-dark-700">{strukturFehler}</div>}

      {/* Kopf: Identitaet + Aktion */}
      <div className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <Link to="/patienten" className="-ml-2 inline-flex min-h-11 items-center px-2 text-xs text-ps-dark-400 transition-colors hover:text-ps-dark-700">← Patienten</Link>
            <h1 className="page-h1 mt-1">{displayName()}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ps-dark-400">
              {detail.stammdaten?.geburtsdatum && <><span>geb. <span className="numerical">{fmtDate(detail.stammdaten.geburtsdatum)}</span></span><span>·</span></>}
              {aktenNr(pid) && <><span>{aktenNr(pid)}</span><span>·</span></>}
              <span><span className="numerical font-semibold text-ps-dark-700">{docFiles.length}</span> Dokumente</span>
              {simedAktiv && stand && <>
                <span>·</span>
                {standLaeuft
                  ? <span className="inline-flex items-center gap-1 text-primary-700"><ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />{stand.status === 'laeuft' ? 'Wird gerade aus siMed aktualisiert …' : 'Aktualisierung aus siMed wartet …'}</span>
                  : <span title={stand.zuletzt ? 'Letzte erfolgreiche Aktualisierung aus siMed' : 'Seit der Umstellung auf PowerCare (08.09.2026) noch nicht über die Warteschlange aktualisiert'}>
                      {stand.zuletzt ? <>aus siMed aktualisiert <span className="numerical text-ps-dark-600">{zeitpunkt(stand.zuletzt)}</span></> : 'aus siMed: Stand unbekannt'}
                    </span>}
                {stand.status === 'fehler' && <span className="text-amber-700" title={stand.meldung}>
                  · letzter Versuch fehlgeschlagen{stand.naechster_versuch ? `, nächster ${zeitpunkt(stand.naechster_versuch)}` : ''}</span>}
              </>}
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {simedAktiv && stand?.darf_anfordern && !standLaeuft && (
              <button onClick={() => void aktualisieren()} disabled={anfordern} className="btn-ghost flex items-center gap-2"
                title="Akte aus siMed aktualisieren — holt nur Neues und Geändertes. Dauert je nach Akte einige Minuten.">
                <ArrowPathIcon className={`h-4 w-4 ${anfordern ? 'animate-spin' : ''}`} /> Aus siMed aktualisieren
              </button>
            )}
            <Link to={`/chat?patient=${encodeURIComponent(pid)}`} className="btn-primary flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-4 w-4" /> Frage zu dieser Akte
            </Link>
          </div>
        </div>
      </div>

      {/* Cockpit + Fragen */}
      <div className="grid items-start gap-4 lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_440px] [&>*]:min-w-0">
        <div className="space-y-4">
          {cockpit?.has_struktur && (
            <div className="grid gap-4 md:grid-cols-2 [&>*]:min-w-0">
              <div className="card">
                <div className="mb-2.5 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-ps-dark-700">{cockpit.diagnosen_ungekennzeichnet ? 'Diagnosen' : 'Wichtige Diagnosen'}</h2>
                  {cockpit.diagnosen_total > 0 && <button onClick={() => openStructured('index.md', 'akte.md')} className="-my-2 inline-flex min-h-11 items-center px-1 text-xs font-medium text-primary-500 hover:underline">alle {cockpit.diagnosen_total} ›</button>}
                </div>
                {cockpit.diagnosen.length ? (<>
                  <ul className="divide-y divide-gray-200">
                    {cockpit.diagnosen.map((d, i) => (
                      <li key={i} className={`py-2.5 first:pt-0 last:pb-0 ${d.wichtig ? '-ml-4 border-l-2 border-l-primary-500 pl-4' : ''}`}>
                        <div className="flex items-baseline gap-3">
                          <span className="numerical w-[68px] shrink-0 text-xs text-ps-dark-400">{d.seit ? `seit ${d.seit}` : '—'}</span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold leading-snug text-ps-dark-900">{d.text}</span>
                            {d.beleg && (
                              <span className="mt-1.5 block rounded-r border-l-2 border-primary-300 bg-gray-50 px-3 py-1.5">
                                <span className="block text-[13px] italic text-ps-dark-700">„{d.beleg}"</span>
                                <span className="mt-1 block text-[11.5px] text-ps-dark-400">{d.beleg_quelle}{d.beleg_datum ? ` · ${d.beleg_datum}` : ''}</span>
                              </span>
                            )}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {cockpit.diagnosen_ungekennzeichnet && <p className="mt-2 text-xs text-ps-dark-400">In SimED ist keine als wichtig gekennzeichnet — gezeigt sind die neuesten.</p>}
                  {cockpit.diagnosen_weitere > 0 && <button onClick={() => openStructured('index.md', 'akte.md')} className="mt-2.5 text-xs text-ps-dark-400 hover:text-primary-500">+ {cockpit.diagnosen_weitere} weitere Diagnosen anzeigen</button>}
                </>) : <div className="text-sm text-ps-dark-400">Keine Diagnosen hinterlegt.</div>}
              </div>

              <div className="card">
                <h2 className="mb-2.5 text-sm font-semibold text-ps-dark-700">Dauermedikation</h2>
                {cockpit.dauermedikation.length ? (
                  <ul className="space-y-2">
                    {cockpit.dauermedikation.map((m, i) => (
                      <li key={i} className="text-sm leading-snug text-ps-dark-900">
                        <div className="flex items-baseline justify-between gap-2">
                          <span className="min-w-0 font-medium">{m.medi}</span>
                          {m.dosierung && <span className="badge badge-neutral numerical shrink-0">{m.dosierung}</span>}
                        </div>
                        {m.bemerkung && <div className="mt-0.5 text-xs text-ps-dark-400">{m.bemerkung}</div>}
                      </li>
                    ))}
                  </ul>
                ) : <div className="text-sm text-ps-dark-400">Keine Dauermedikation erfasst.</div>}
              </div>

              {cockpit.labor_aktuell.length > 0 && (
                <div className="card">
                  <div className="mb-2.5 flex items-center justify-between">
                    <h2 className="text-sm font-semibold text-ps-dark-700">Aktuelle Laborwerte</h2>
                    <button onClick={() => openStructured('labor.md')} className="-my-2 inline-flex min-h-11 items-center px-1 text-xs font-medium text-primary-500 hover:underline">Verlauf ›</button>
                  </div>
                  <div className="grid grid-cols-2 gap-x-5 gap-y-1.5">
                    {cockpit.labor_aktuell.map((l, i) => (
                      <div key={i} className="flex items-baseline justify-between gap-2 border-b border-dotted border-gray-200 py-0.5">
                        <span className="min-w-0 truncate text-xs text-ps-dark-500" title={l.parameter}>{l.parameter}</span>
                        {l.flag === 'hoch' || l.flag === 'tief'
                          ? <span className={`badge numerical shrink-0 ${l.flag === 'hoch' ? 'border-rose-200 bg-rose-50 text-rose-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>{l.wert} {FLAG_ARROW[l.flag]}</span>
                          : <span className="numerical shrink-0 whitespace-nowrap text-sm font-medium text-ps-dark-900">{l.wert}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="card">
                <h2 className="mb-2.5 text-sm font-semibold text-ps-dark-700">Allergien</h2>
                {cockpit.allergien.length
                  ? <div className="mb-4 flex flex-wrap gap-1.5">{cockpit.allergien.map((a, i) => <StatusBadge key={i} variant="error" size="sm" label={a.staerke ? `${a.allergie} · ${a.staerke}` : a.allergie} />)}</div>
                  : <div className="mb-4 text-sm text-ps-dark-400">Keine erfasst.</div>}
                {cockpit.letzte_kontakte.length > 0 && (<>
                  <h2 className="mb-2 text-sm font-semibold text-ps-dark-700">📅 Letzte Kontakte</h2>
                  <ul className="space-y-1">
                    {cockpit.letzte_kontakte.map((k, i) => (
                      <li key={i} className="flex items-baseline justify-between gap-2 text-sm">
                        <span className="min-w-0 truncate text-ps-dark-900">{cap(k.fachgebiet || k.typ)}</span>
                        <span className="numerical shrink-0 text-xs text-ps-dark-400">{fmtDate(k.datum)}</span>
                      </li>
                    ))}
                  </ul>
                </>)}
              </div>
            </div>
          )}

          {stammRows.length > 0 && (
            <details className="card p-0">
              <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-ps-dark-700 hover:text-ps-dark-900">Stammdaten</summary>
              <div className="grid gap-x-8 gap-y-2 px-4 pb-4 sm:grid-cols-2 lg:grid-cols-3">
                {stammRows.map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-3 border-b border-dotted border-gray-200 py-1">
                    <span className="text-xs capitalize text-ps-dark-400">{k.replaceAll('_', ' ')}</span>
                    <span className="text-right text-sm text-ps-dark-900">{v}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>

        <aside className="space-y-4 lg:sticky lg:top-4"><AkteChats pid={pid} /><AkteFragen pid={pid} patientName={displayName()} /></aside>
      </div>

      {/* Dokumentliste + Viewer */}
      <div className="grid items-start gap-4 lg:grid-cols-[340px_minmax(0,1fr)] xl:grid-cols-[380px_minmax(0,1040px)] [&>*]:min-w-0">
        <div className="card overflow-hidden p-0 lg:max-h-[70vh] lg:overflow-y-auto">
          <div className="sticky top-0 z-10 border-b border-gray-200 bg-white">
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <h2 className="text-sm font-semibold text-ps-dark-700">Dokumente</h2>
              <div className="flex items-center gap-2">
                <span className="text-xs text-ps-dark-400">{sichtbareDocs.length}{jahrFilter ? ` von ${docFiles.length}` : ''}</span>
                <label className={`inline-flex h-7 cursor-pointer items-center gap-1 rounded-md border border-gray-200 px-2 text-[11.5px] font-medium transition-colors ${
                  ladeDok ? 'cursor-wait opacity-60' : 'text-ps-dark-700 hover:border-primary-500 hover:bg-primary-50 hover:text-primary-500'}`}
                  title="PDF, Scan oder Foto in diese Akte legen — wird gelesen, abgelegt und nachgeführt">
                  <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                  <span>{ladeDok ? 'Wird gelesen …' : 'Hinzufügen'}</span>
                  <input type="file" multiple className="hidden" disabled={ladeDok}
                    accept=".pdf,.docx,.odt,.png,.jpg,.jpeg,.webp,.tiff,.tif,.heic,.csv,.md,.txt"
                    onChange={(e) => { void dokumenteHochladen(e.currentTarget.files); e.currentTarget.value = ''; }} />
                </label>
              </div>
            </div>
            {dokMeldung && (
              <div className={`mx-3 mb-2 flex items-start gap-1.5 rounded border px-2.5 py-2 text-[11.5px] ${
                dokMeldung.ok ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
                {dokMeldung.ok ? <CheckCircleIcon className="mt-px h-3.5 w-3.5 shrink-0" />
                               : <ExclamationTriangleIcon className="mt-px h-3.5 w-3.5 shrink-0" />}
                <span className="flex-1">{dokMeldung.text}</span>
                <button onClick={() => setDokMeldung(null)} className="shrink-0 px-0.5 opacity-60 hover:opacity-100" aria-label="Schliessen">×</button>
              </div>
            )}
            {(jahre.length > 1 || kategorien.length > 1) && (
              <>
                <div className="flex items-center gap-1 px-3 pb-2 text-[11px]">
                  <span className="text-ps-dark-400">Filtern nach</span>
                  {(['jahr', 'kategorie'] as const).map((g) => (
                    <button key={g} onClick={() => setGruppierung(g)} aria-pressed={gruppierung === g}
                      className={`rounded px-1.5 py-0.5 font-medium transition-colors ${gruppierung === g
                        ? 'bg-gray-100 text-ps-dark-800' : 'text-ps-dark-400 hover:text-ps-dark-700'}`}>
                      {g === 'jahr' ? 'Jahr' : 'Kategorie'}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5 px-3 pb-2.5">
                  {gruppierung === 'jahr' ? (
                    <>
                      {jahrKnopf('', 'Alle', jahrFilter === '')}
                      {jahre.map(([j, n]) => jahrKnopf(j, j, jahrFilter === j, `${n} ${n === 1 ? 'Dokument' : 'Dokumente'}`))}
                      {ohneDatum > 0 && jahrKnopf('ohne', 'ohne Datum', jahrFilter === 'ohne', `${ohneDatum} ohne erkennbares Datum`)}
                    </>
                  ) : (
                    <>
                      {katKnopf('', 'Alle', katFilter === '')}
                      {kategorien.map(([k, n]) => katKnopf(k, `${kurzeKategorie(k)} ${n}`, katFilter === k, `${k} — ${n} ${n === 1 ? 'Dokument' : 'Dokumente'}`))}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          {structuredFiles.map((f) => (
            <button key={f.name} onClick={() => void openFile(f)}
              className={`flex w-full items-center gap-2 border-b border-gray-200 px-4 py-2.5 text-left transition-colors ${activeFile?.name === f.name ? 'border-l-2 border-l-primary-500 bg-gray-50' : 'hover:bg-gray-50'}`}>
              <span className="badge badge-neutral shrink-0">Übersicht</span>
              <span className="text-sm font-medium leading-snug text-ps-dark-900">{STRUCTURED_LABEL[f.name]}</span>
            </button>
          ))}
          {docFiles.length === 0 && structuredFiles.length === 0 && <div className="px-4 py-8 text-center text-sm text-ps-dark-400">Noch keine Dokumente.</div>}
          {docFiles.length > 0 && sichtbareDocs.length === 0 && <div className="px-4 py-8 text-center text-sm text-ps-dark-400">{gruppierung === 'jahr' ? 'Keine Dokumente aus diesem Jahr.' : 'Keine Dokumente in dieser Kategorie.'}</div>}

          {sichtbareDocs.map((f) => (
            <button key={f.name} onClick={() => void openFile(f)}
              className={`min-h-14 w-full border-b border-gray-200 px-4 py-3 text-left transition-colors last:border-b-0 ${activeFile?.name === f.name ? 'border-l-2 border-l-primary-500 bg-gray-50' : 'hover:bg-gray-50'}`}>
              {/* Datum OBEN: beim Durchscrollen sucht man zuerst nach dem Zeitpunkt. */}
              <span className="mb-0.5 flex items-center gap-2 text-xs text-ps-dark-400">
                <span className="numerical">{fmtDate(f.datum) === '—' ? 'ohne Datum' : fmtDate(f.datum)}</span>
                {f.kategorie && <span className="truncate">· {f.kategorie}</span>}
              </span>
              <div className="flex items-center justify-between gap-2">
                <span className="min-w-0 break-words text-sm font-medium leading-snug text-ps-dark-900">{docTitle(f)}</span>
                {f.attachment && !RENDERABLE_ATT.test(f.attachment) && <span className="badge badge-neutral shrink-0" title="Nur als Download – Vorschau zeigt den extrahierten Text">{ext(f.attachment)}</span>}
              </div>
            </button>
          ))}
        </div>

        <div className="card flex min-h-[300px] flex-col p-0 lg:min-h-[60vh]">
          {!activeFile ? (
            <div className="flex flex-1 items-center justify-center p-8 text-sm text-ps-dark-400">Dokument links auswählen</div>
          ) : (<>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-3">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-ps-dark-900" title={activeFile.name}>{docTitle(activeFile)}</div>
                <div className="flex gap-2 text-xs text-ps-dark-400">
                  <span className="numerical">{fmtDate(activeFile.datum)}</span>
                  {activeFile.fachgebiet && !docTitle(activeFile).toLowerCase().includes(activeFile.fachgebiet.toLowerCase()) && <span>· {activeFile.fachgebiet}</span>}
                </div>
              </div>
              {activeFile.attachment && (
                <a href={patientAttachmentUrl(pid, activeFile.attachment)} target="_blank" rel="noopener" download className="btn-ghost flex shrink-0 items-center gap-1 text-xs" title="Original-Datei herunterladen">
                  <ArrowDownTrayIcon className="h-3.5 w-3.5" /> Original{RENDERABLE_ATT.test(activeFile.attachment) ? '' : ` (${ext(activeFile.attachment)})`}
                </a>
              )}
            </div>

            {showPdf && activeFile.attachment ? (
              <div className="grid gap-3 p-3 xl:grid-cols-2">
                <div className="h-[55vh] min-w-0 xl:h-[78vh]">
                  <PdfViewer src={patientAttachmentUrl(pid, anzeigeDatei(activeFile) as string)} title={activeFile.attachment} minHeight="100%" />
                </div>
                <div className="flex min-w-0 flex-col rounded-lg border border-gray-200 bg-gray-50">
                  <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2 text-xs font-semibold text-ps-dark-500">
                    Erkannter Inhalt
                    {activeFile.textquelle && <span className="badge badge-neutral" title="Woher der Text stammt">{activeFile.textquelle}</span>}
                    {activeFile.zeichen ? <span className="numerical text-ps-dark-400">{activeFile.zeichen} Zeichen</span> : null}
                  </div>
                  {fileLoading ? <Shimmer /> : fileText
                    ? <div className="h-[45vh] overflow-auto p-4 xl:h-[calc(78vh-2.5rem)]"><MarkdownContent text={cleanMd(fileText)} /></div>
                    : <div className="p-4 text-sm text-ps-dark-400">Für dieses Dokument wurde kein Text erkannt — bei reinen Fotos ist das normal.</div>}
                </div>
              </div>
            ) : fileLoading ? <Shimmer /> : fileText ? (<>
              {activeFile.attachment && !RENDERABLE_ATT.test(activeFile.attachment) && (
                <div className="mx-4 -mb-1 mt-3 flex flex-wrap items-center gap-2 rounded bg-gray-50 px-3 py-2 text-xs text-ps-dark-400">
                  <span>Keine Vorschau für {ext(activeFile.attachment)}-Dateien – unten der automatisch extrahierte Text. Das Original oben herunterladen.</span>
                  {activeFile.textquelle && <span className="badge badge-neutral">{activeFile.textquelle}</span>}
                  {activeFile.zeichen ? <span className="numerical">{activeFile.zeichen} Zeichen</span> : null}
                </div>
              )}
              <div className="overflow-auto p-6 lg:max-h-[62vh]"><MarkdownContent text={cleanMd(fileText)} /></div>
            </>) : <div className="flex flex-1 items-center justify-center p-8 text-sm text-ps-dark-400">Dokument konnte nicht geladen werden.</div>}
          </>)}
        </div>
      </div>
    </div>
  );
}
